import { Trans, useTranslation } from "react-i18next";
import { Link } from "~/components/ui/link";
import { Button } from "~/components/ui/button";
import { Picture } from "~/components/ui/picture";
import { LINE_BREAKS } from "~/components/ui/max-width-line-break";
import { cn } from "~/lib/utils";
import { imageUrl } from "~/lib/image-url";

// Plain public/ paths so Picture can serve the AVIF/WebP siblings. The mobile file is
// the narrow center crop from the mobile frame, used below sm.
const heroImg = imageUrl("/images/fleet/hero.jpg");
const heroImgMobile = imageUrl("/images/fleet/hero-mobile.jpg");

// Hero CTAs are 16px bold: 52px tall in equal grid columns on mobile, 60px x 190px
// from lg. Overrides the shared Button sizes on these instances only.
const ctaClassName =
    "h-[52px] w-full px-4 py-4 text-base leading-5 tracking-[0.16px] lg:h-[60px] lg:w-[190px] lg:px-6 lg:py-5";

// Must render inside the .fleet token scope (the /fleet route's wrapper).
export function FleetHero() {
    const { t } = useTranslation("fleet");

    return (
        <section data-header-hero aria-labelledby="fleet-hero-title" className="relative h-[532px] overflow-hidden">
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <Picture
                    src={heroImg}
                    mobileSrc={heroImgMobile}
                    desktopMedia="(min-width: 640px)"
                    alt=""
                    fetchPriority="high"
                    loading="eager"
                    className="absolute inset-0 size-full object-cover opacity-50"
                />
                <div className="from-figma-blue-midnight/96 via-figma-blue-midnight/82 to-figma-blue-midnight/25 absolute inset-0 bg-gradient-to-b via-44% lg:bg-gradient-to-r" />
            </div>

            <div className="wrap relative flex h-full items-center">
                <div className="flex w-full flex-col items-center gap-8 text-center lg:max-w-[755px] lg:items-start lg:gap-12 lg:text-left">
                    <div className="flex flex-col gap-3">
                        <h1
                            id="fleet-hero-title"
                            className="font-display text-text-heading text-[32px] leading-[1.15] font-extrabold lg:text-[56px] lg:leading-[1.1]"
                        >
                            <Trans t={t} i18nKey="hero.title" components={LINE_BREAKS} />
                        </h1>
                        {/* Figma's mobile body runs 4px past the content box on each side (351px in 343px). */}
                        <p className="text-text-body-secondary text-md leading-[1.1] max-lg:-mx-1 lg:max-w-[509px] lg:leading-[1.2]">
                            {t("hero.description")}
                        </p>
                    </div>
                    <div className="grid w-full grid-cols-2 gap-3 lg:flex lg:w-auto lg:gap-6">
                        <Button asChild variant="primary-alt" className={cn(ctaClassName, "max-lg:order-last")}>
                            <Link data-sid="fleet-hero-build-quote" to="/fleet/quote">{t("hero.build_quote")}</Link>
                        </Button>
                        <Button asChild variant="secondary-alt" className={ctaClassName}>
                            <Link data-sid="fleet-hero-contact-sales" to="/contact">{t("hero.contact_sales")}</Link>
                        </Button>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default FleetHero;
